/****************************************************************************************************************
* ©2018 EdgeVerve Systems Limited, Bangalore, India. All Rights Reserved.
*
* The EdgeVerve proprietary software program ("Program"), is protected by copyrights laws, international treaties and
* other pending or existing intellectual property rights in India, the United States and other countries. The Program
* may contain / reference third party or open source components, the rights to which continue to remain with the
* applicable third party licensors or the open source community as the case may be and nothing here transfers the
* rights to the third party and open source components, except as expressly permitted. Any unauthorized reproduction,
* storage, transmission in any form or by any means (including without limitation to electronic, mechanical, printing,
* photocopying, recording or otherwise), or any distribution of this Program, or any portion of it, may result in
* severe civil and criminal penalties, and will be prosecuted to the maximum extent possible under the law.
*****************************************************************************************************************/

import {
  Injectable,
  ViewContainerRef,
  Injector,
  Type
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

import { Constants } from '../shared.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ComponentDialogComponent } from '../components/dynamic-component/component-dialog/component-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from '../../core/services/profile.service';
import { SuppUsersListComponent } from '../../ba-supplier-ui/components/supp-users-list/supp-users-list.component';


@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private entityAttributeUrl = Constants.API_COMMON_URL + Constants.ENTITY_BASE_URL + '/getEntityAttributes/';
  private getScreenUrl = Constants.API_COMMON_URL + Constants.SCREEN_BASE_URL + '/get';
  private saveScreenUrl = Constants.API_COMMON_URL + Constants.SCREEN_BASE_URL + '/save';


  private componentRegistry = new Map<string, Type<any>>([
    ['supp-users-list', SuppUsersListComponent],
    // Register ALL dynamic components here
  ]);

  constructor(
    private httpClient: HttpClient,
    private injector: Injector,
    public snackBar: MatSnackBar,
    public dialog: MatDialog,
    private translator: TranslateService,
    private profileService: ProfileService
  ) {
    this.translator.setDefaultLang(Constants.DEFAULT_LANGUAGE);
    this.translator.use(this.profileService.getLanguageCode());
  }

  /**
   * FORM VALIDATION
   */
  validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);

      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });

      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  /**
   * FIXED DYNAMIC COMPONENT LOADER
   * Keeps 4 params → NO CHANGE needed anywhere else
   */
  createDynamicComponent(
    modulePath: string,   // kept for backward compatibility (unused)
    componentName: string,
    viewContainerRef: ViewContainerRef,
    callback
  ) {
    viewContainerRef.clear();

    const component = this.componentRegistry.get(componentName);

    if (!component) {
      const error = `Component not found: ${componentName}`;
      console.error(error);
      callback(error, null);
      return;
    }

    try {
      const componentRef = viewContainerRef.createComponent(component, {
        injector: this.injector
      });

      callback(null, componentRef);

    } catch (e) {
      console.error('Error creating component:', e);
      callback(e, null);
    }
  }

  /**
   * SNACKBAR
   */
  openSnackBar(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  /**
   * API CALLS
   */
  getEntityAttributes(pageId: string): Observable<any> {
    return this.httpClient.get<any>(this.entityAttributeUrl + pageId);
  }

  getScreen(id: number, screenId: string, design: boolean, type: string) {
    let url = `${this.getScreenUrl}?screenId=${screenId}&design=${design}`;

    if (id) url += `&id=${id}`;
    if (type) url += `&type=${type}`;

    return this.httpClient.get<any>(url);
  }

  saveScreen(screen: any): Observable<any> {
    return this.httpClient.post<any>(this.saveScreenUrl, screen);
  }

  /**
   * DIALOG
   */
  openDialog(title, content, callback): void {
    const dialogRef = this.dialog.open(ComponentDialogComponent, {
      width: '768px',
      data: { title, content }
    });

    dialogRef.afterClosed().subscribe(result => {
      callback(result);
    });
  }

  /**
   * TRANSLATION
   */
  async translate(code, param = {}) {
    return this.translator.get(code, param).toPromise();
  }
}



